import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiCircle,
  FiClock,
  FiLock,
  FiMail,
  FiMessageCircle,
  FiUser,
} from "react-icons/fi";
import { selectCurrentCompanyId } from "../../../store/auth";
import {
  useForgotPasswordMutation,
  useResendOTPViaWhatsAppMutation,
  useResetPasswordMutation,
  useVerifyResetOtpMutation,
} from "../../../store/auth/authApi";
import { normalizePhoneNumber } from "../../../utils/phoneUtils";
import {
  ActionBar,
  Alert,
  Button,
  OtpInput,
  PasswordField,
  StepProgress,
  TextField,
  FocusFirstError,
} from "../../Auth";

// Matches the backend window: a recovery code stays valid for 5 minutes.
const OTP_RESEND_SECONDS = 150;
const OTP_LENGTH = 6;
const DEFAULT_COUNTRY_CODE = "234";
const STEPS = ["Find account", "Enter code", "New password"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const identifierSchema = Yup.object({
  identifier: Yup.string()
    .trim()
    .required("Enter your email or phone number")
    .test("is-email-or-phone", "Enter a valid email or phone number", (value) => {
      if (!value) return false;
      if (EMAIL_RE.test(value)) return true;
      const cleaned = value.replace(/[\s\-()]/g, "");
      return [/^\+?\d{10,15}$/, /^0\d{9,10}$/, /^\d{10,11}$/].some((re) => re.test(cleaned));
    }),
});

const passwordSchema = Yup.object({
  newPassword: Yup.string()
    .min(8, "Use at least 8 characters")
    .required("Enter a new password"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), null], "The two passwords do not match")
    .required("Type the new password again"),
});

/** Pull the server's message out of an RTK Query result, or fall back to ours. */
function serverMessage(response, fallback) {
  if (response?.error) {
    return response.error?.data?.message || response.error?.data?.error || fallback;
  }
  if (response?.data?.status === false) {
    return response.data?.message || fallback;
  }
  return null;
}

function maskIdentifier(value, type) {
  if (!value) return "";
  if (type === "email" && value.includes("@")) {
    const [name, domain] = value.split("@");
    return `${name.slice(0, 3)}****@${domain}`;
  }
  return value.length > 7 ? `${value.slice(0, 4)}****${value.slice(-3)}` : value;
}

function formatTime(total) {
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export default function ForgetPasswordForm() {
  const navigate = useNavigate();
  const companyId = useSelector(selectCurrentCompanyId);

  const [step, setStep] = useState("find"); // find | code | password | done
  const [identifier, setIdentifier] = useState("");
  const [identifierType, setIdentifierType] = useState("email");
  const [userInfo, setUserInfo] = useState(null);
  const [pin, setPin] = useState("");
  const [seconds, setSeconds] = useState(0);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [notice, setNotice] = useState("");
  const [otpKey, setOtpKey] = useState(0);

  const [forgotPassword] = useForgotPasswordMutation();
  const [resendViaWhatsApp] = useResendOTPViaWhatsAppMutation();
  const [verifyOtp] = useVerifyResetOtpMutation();
  const [resetPassword] = useResetPasswordMutation();

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const id = setInterval(() => setSeconds((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [seconds]);

  const userIdOf = (info) => info?.id || info?.staff_id || info?.member_id;

  /** Ask the server to send a code. Returns true on success. */
  async function requestCode(rawIdentifier, { silent = false } = {}) {
    if (!companyId) {
      setFormError("We could not load this cooperative. Refresh the page and try again.");
      return false;
    }
    const isEmail = EMAIL_RE.test(rawIdentifier);
    let value = rawIdentifier;
    if (!isEmail) {
      const normalized = normalizePhoneNumber(rawIdentifier, DEFAULT_COUNTRY_CODE);
      if (!normalized || !normalized.startsWith("+")) {
        setFormError("Enter a valid phone number or email address.");
        return false;
      }
      value = normalized;
    }

    setBusy(true);
    setFormError("");
    try {
      const response = await forgotPassword({
        company_id: companyId,
        ...(isEmail ? { email: value } : { phone: value }),
      });
      const problem = serverMessage(response, "We could not send a code. Please try again.");
      if (problem || !response.data?.data) {
        setFormError(problem || "We could not send a code. Please try again.");
        return false;
      }
      setIdentifier(value);
      setIdentifierType(isEmail ? "email" : "phone");
      setUserInfo(response.data.data);
      setSeconds(OTP_RESEND_SECONDS);
      setPin("");
      setOtpKey((k) => k + 1);
      if (!silent) setNotice("");
      return true;
    } catch (error) {
      console.error("Error sending recovery code:", error);
      setFormError("Something went wrong. Check your connection and try again.");
      return false;
    } finally {
      setBusy(false);
    }
  }

  async function handleFind(values) {
    const ok = await requestCode(values.identifier.trim());
    if (ok) setStep("code");
  }

  async function handleResend() {
    if (seconds > 0 || busy) return;
    const ok = await requestCode(identifier, { silent: true });
    if (ok) {
      setNotice(
        identifierType === "email"
          ? "We sent a new code to your email."
          : "We sent a new code by SMS."
      );
    }
  }

  async function handleResendWhatsApp() {
    if (seconds > 0 || busy || identifierType !== "phone") return;
    const normalized = normalizePhoneNumber(identifier, DEFAULT_COUNTRY_CODE);
    if (!normalized) {
      setFormError("Enter a valid phone number first.");
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      const response = await resendViaWhatsApp({ company_id: companyId, phone: normalized });
      const problem = serverMessage(response, "We could not send the code on WhatsApp.");
      if (problem || !response.data?.data) {
        setFormError(problem || "We could not send the code on WhatsApp.");
        return;
      }
      setUserInfo(response.data.data);
      setSeconds(OTP_RESEND_SECONDS);
      setPin("");
      setOtpKey((k) => k + 1);
      setNotice("We sent a new code to your WhatsApp.");
    } catch (error) {
      console.error("Error resending code on WhatsApp:", error);
      setFormError("Something went wrong. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleVerify(event) {
    if (event) event.preventDefault();
    if (pin.length !== OTP_LENGTH || busy) return;
    const userId = userIdOf(userInfo);
    if (!userId) {
      setStep("find");
      setFormError("We lost track of your request. Please start again.");
      return;
    }
    setBusy(true);
    setFormError("");
    setNotice("");
    try {
      const response = await verifyOtp({ companyId, userId, otp: pin });
      const problem = serverMessage(response, "That code is not right.");
      if (problem) {
        const expired = /expired/i.test(problem);
        setFormError(
          expired
            ? "That code has expired. Request a new one."
            : "That code is not right. Check it and try again."
        );
        setPin("");
        setOtpKey((k) => k + 1);
        return;
      }
      setStep("password");
    } catch (error) {
      console.error("Error verifying code:", error);
      setFormError("Something went wrong. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  async function handleReset(values) {
    const userId = userIdOf(userInfo);
    if (!userId) {
      setStep("find");
      setFormError("We lost track of your request. Please start again.");
      return;
    }
    setBusy(true);
    setFormError("");
    try {
      const response = await resetPassword({
        companyId,
        userId,
        email: userInfo?.email || identifier, // the API looks the account up with this
        otp: pin,
        password: values.newPassword,
      });
      if (response.error) {
        const message = response.error?.data?.message || "";
        if (/otp|passcode|pin|expired/i.test(message)) {
          // The code went stale between steps. Send them back to enter a fresh one.
          setPin("");
          setOtpKey((k) => k + 1);
          setStep("code");
          setFormError("Your code expired. Request a new one to continue.");
          return;
        }
        setFormError(message || "We could not update your password. Please try again.");
        return;
      }
      setStep("done");
    } catch (error) {
      console.error("Error resetting password:", error);
      setFormError("Something went wrong. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  }

  const stepIndex = { find: 0, code: 1, password: 2 }[step];
  const waiting = seconds > 0;

  if (step === "done") {
    return (
      <div className="ax-card ax-done">
        <div className="ax-done-badge" aria-hidden="true">
          <FiCheckCircle size={32} />
        </div>
        <div>
          <h1 className="ax-h1">Password updated</h1>
          <p className="ax-sub">You can now sign in with your new password.</p>
        </div>
        <Button onClick={() => navigate("/login")}>Sign in</Button>
      </div>
    );
  }

  return (
    <>
      <div className="ax-topbar">
        {step === "find" ? (
          <Link className="ax-link" to="/login">
            <FiArrowLeft aria-hidden="true" size={16} />
            Back to sign in
          </Link>
        ) : step === "code" ? (
          <button
            type="button"
            className="ax-link"
            onClick={() => {
              setStep("find");
              setFormError("");
              setNotice("");
            }}
          >
            <FiArrowLeft aria-hidden="true" size={16} />
            Change email or phone
          </button>
        ) : (
          <Link className="ax-link" to="/login">
            Cancel
          </Link>
        )}
      </div>

      <StepProgress steps={STEPS} current={stepIndex} />

      <div className="ax-card">
        {formError ? <Alert tone="error">{formError}</Alert> : null}

        {step === "find" ? (
          <>
            <div>
              <h1 className="ax-h1">Reset your password</h1>
              <p className="ax-sub">
                Enter the email or phone number on your account and we will send you a 6-digit code.
              </p>
            </div>
            <Formik
              initialValues={{ identifier: "" }}
              validationSchema={identifierSchema}
              onSubmit={handleFind}
            >
              {() => (
                <Form
                  noValidate
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <FocusFirstError />
                  <TextField
                    name="identifier"
                    label="Email or phone number"
                    icon={FiUser}
                    autoComplete="username"
                    autoCapitalize="none"
                    spellCheck={false}
                    inputMode="email"
                    placeholder="Email or phone number"
                    hint="Use your email, or a phone number like 08012345678."
                  />
                  <Button type="submit" loading={busy}>
                    Send code
                  </Button>
                </Form>
              )}
            </Formik>
          </>
        ) : null}

        {step === "code" ? (
          <form
            onSubmit={handleVerify}
            noValidate
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div>
              <h1 className="ax-h1">Enter the 6-digit code</h1>
              <p className="ax-sub">
                We sent it {identifierType === "email" ? "by email" : "by SMS"} to{" "}
                <strong>{maskIdentifier(identifier, identifierType)}</strong>. It stays valid for 5
                minutes.
              </p>
            </div>

            {notice ? <Alert tone="success">{notice}</Alert> : null}

            <OtpInput
              key={otpKey}
              value={pin}
              onChange={(next) => {
                setPin(next);
                if (formError) setFormError("");
              }}
              length={OTP_LENGTH}
              disabled={busy}
              invalid={Boolean(formError)}
              autoFocus
            />

            <Button type="submit" loading={busy} disabled={pin.length !== OTP_LENGTH}>
              Continue
            </Button>

            <div className="ax-divider">Did not get it?</div>

            <button
              type="button"
              className="ax-choice"
              onClick={handleResend}
              disabled={waiting || busy}
            >
              {identifierType === "email" ? (
                <FiMail aria-hidden="true" size={18} />
              ) : (
                <FiMessageCircle aria-hidden="true" size={18} />
              )}
              {identifierType === "email" ? "Send the code again" : "Send the code again by SMS"}
              {waiting ? (
                <small>
                  <FiClock aria-hidden="true" size={13} style={{ marginRight: 4 }} />
                  {formatTime(seconds)}
                </small>
              ) : null}
            </button>

            {identifierType === "phone" ? (
              <button
                type="button"
                className="ax-choice"
                onClick={handleResendWhatsApp}
                disabled={waiting || busy}
              >
                <FiMessageCircle aria-hidden="true" size={18} />
                Send the code on WhatsApp
                {waiting ? <small>{formatTime(seconds)}</small> : null}
              </button>
            ) : null}
          </form>
        ) : null}

        {step === "password" ? (
          <>
            <div>
              <h1 className="ax-h1">Choose a new password</h1>
              <p className="ax-sub">Your code was accepted. Pick a password you have not used here before.</p>
            </div>
            <Formik
              initialValues={{ newPassword: "", confirmPassword: "" }}
              validationSchema={passwordSchema}
              onSubmit={handleReset}
            >
              {({ values }) => {
                const lengthMet = values.newPassword.length >= 8;
                const matchMet =
                  Boolean(values.confirmPassword) && values.newPassword === values.confirmPassword;
                return (
                <Form
                  noValidate
                  style={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <FocusFirstError />
                  <PasswordField
                    name="newPassword"
                    label="New password"
                    icon={FiLock}
                    autoComplete="new-password"
                    placeholder="At least 8 characters"
                  />
                  <PasswordField
                    name="confirmPassword"
                    label="Confirm new password"
                    icon={FiLock}
                    autoComplete="new-password"
                    placeholder="Type it again"
                  />
                  <ul className="ax-rules" aria-label="Password checks">
                    <li className={lengthMet ? "is-met" : ""}>
                      {lengthMet ? <FiCheck aria-hidden="true" size={16} /> : <FiCircle aria-hidden="true" size={16} />}
                      At least 8 characters
                      <span className="ax-sr">{lengthMet ? ", done" : ", not yet"}</span>
                    </li>
                    <li className={matchMet ? "is-met" : ""}>
                      {matchMet ? <FiCheck aria-hidden="true" size={16} /> : <FiCircle aria-hidden="true" size={16} />}
                      Both passwords match
                      <span className="ax-sr">{matchMet ? ", done" : ", not yet"}</span>
                    </li>
                  </ul>
                  <ActionBar>
                    <Button type="submit" loading={busy}>
                      Update password
                    </Button>
                  </ActionBar>
                </Form>
                );
              }}
            </Formik>
          </>
        ) : null}
      </div>
    </>
  );
}
